/* eslint-disable react/jsx-closing-tag-location */
import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';

import Button from '../../Button';
import Paragraph from '../../Paragraph';
import Readmore from '../../Paragraph/Readmore';
import Heading from '../../Heading';
import Progress from '../../Progress';
import { renderField } from '../../Inputs';
import { updatePathway } from '../../../Actions/Password/register';

import iphoneImg from '../../../images/iphone-mylivewell.png';

const calcBMI = (kg, cm) => {
  const bmi = parseFloat(kg) / ((parseFloat(cm) / 100) * (parseFloat(cm) / 100));
  return bmi.toFixed(2);
};

class AllResults extends React.PureComponent {
  componentDidMount() {
    if (window.pageYOffset > this.sectionRef.offsetTop) {
      window.scrollTo({ top: this.sectionRef.offsetTop, behavior: 'smooth' });
    }
    this.props.handleSubmit();
  }

  togglePathway(data) {
    this.props.dispatch(updatePathway(data));
  }

  render() {
    const {
      handleSubmit, smoking, smokingstop, smokingamount, weight, height, pregnant, score, activity, units, ethnicity,
    } = this.props;

    const bmi = calcBMI(weight, height);

    let alcoholTitle = '';
    let alcoholStrap = '';
    let alcoholBody = [];
    let activityTitle = '';
    let activityStrap = '';
    let activityBody = [];
    let smokeTitle = '';
    let smokeStrap = '';
    let smokeBody = [];
    let bmiTitle = '';
    let bmiStrap = '';
    let bmiBody = [];

    const bmiUnderweight = 18.5;
    let bmiOverweight = 25;
    let bmiObeseweight = 30;
    switch (ethnicity) {
      case 'Asian or Asian British - Any other Asian background':
      case 'Asian or Asian British - Bangladeshi':
      case 'Asian or Asian British - Indian':
      case 'Asian or Asian British - Pakistani':
      case 'Other Ethnic Groups - Chinese':
      case '5':
      case '6':
      case '7':
      case '8':
      case '17':
        bmiOverweight = 23;
        bmiObeseweight = 27.5;
        break;
      default:
        break;
    }

    // PREGNANT
    if (pregnant === 'Yes') {
      // ALCOHOL + PREGNANT
      if (units === 0) {
        alcoholTitle = 'You\'ve aced this one!';
        alcoholStrap = 'You\'re doing great!';
        alcoholBody = [
          <Paragraph key="id-1">Not drinking alcohol during pregnancy is the
            best way to minimise the risk of health problems to you and your baby. Keep up the good
            work.</Paragraph>,
        ];
      } else if (units > 0 && units <= 7) {
        alcoholTitle = 'We can help make a difference!';
        alcoholStrap = 'We can help.';

        alcoholBody = [
          <Paragraph key="id-1">During pregnancy it is recommended that
            you {"don't"} drink alcohol at all. Drinking whilst pregnant can affect
            your {"baby's"} development, as he or she is unable to process alcohol in the same way as you
            do.</Paragraph>,
          <Paragraph key="id-2">If you have had a drink before you realised
            this, {"don't"} worry too much, but from now on {"it's"} best to stop drinking alcohol.</Paragraph>,
          <Paragraph key="id-3">If {"you'd"} like some extra support, contact
            our team to talk about how a LiveWell Coach could help.</Paragraph>,
        ];
      } else if (units > 7 && units <= 19) {
        alcoholTitle = 'We can help make a difference!';
        alcoholStrap = 'We can help.';

        alcoholBody = [
          <Paragraph key="id-1">{"It's"} recommended that you do not drink alcohol
            at all during pregnancy, as this can harm your {"baby's"} development. We encourage you
            eliminate alcohol whilst you are pregnant.</Paragraph>,
          <Paragraph key="id-2">What changes could you make to cut out alcohol
            at this time?</Paragraph>,
          <Paragraph key="id-3">If {"you'd"} like some extra support, contact our
            team to talk about how a LiveWell Coach could help.</Paragraph>,
        ];
      } else {
        alcoholTitle = 'Careful: You and your unborn baby are at risk';
        alcoholStrap = 'We can help.';

        alcoholBody = [
          <Paragraph key="id-1">You are drinking at levels which could harm
            you and your baby. {"It's"} possible that you have developed a dependence on alcohol. We
            recommend that you contact your midwife about this. They can refer you to a specialist
            service to explore whether you are suffering from alcohol dependency.</Paragraph>,
          <Paragraph key="id-2">You can also give our friendly team a
            call to find out about the support available.</Paragraph>,
        ];
      }

      // ACTIVITY + PREGNANT
      if (activity >= 0 && activity < 4) {
        activityTitle = 'We can help make a difference';
        activityStrap = 'We can help.';
        activityBody = [
          <Paragraph key="id-1">Being active on a regular basis can be tough,
            but it works wonders for our health. {"It's"} recommended that we take 30 minutes of moderate
            exercise, five days a week.</Paragraph>,
          <Paragraph key="id-2">During pregnancy, {"it's"} important to look after
            yourself and not do anything too strenuous, but it’s generally safe to keep up any activity you
            were doing before you were pregnant.</Paragraph>,
          <Paragraph key="id-3">If {"you're"} not used to exercising, half an hour
            of walking a day will do wonders to support a healthy pregnancy and boost your wellbeing.</Paragraph>,
        ];
      } else if (activity === 4) {
        activityTitle = 'Well done - not bad!';
        activityStrap = 'You\'re getting there!';
        activityBody = [
          <Paragraph key="id-1">We encourage you to make activity a regular
            habit. {"It's"} recommended that we take 30 minutes of moderate exercise, five days a week, so
            you’re pretty close already.</Paragraph>,
          <Paragraph key="id-2">Maintaining your usual activity during
            pregnancy is a great way to stay fit and healthy. {"It's"} safe to stay active for as long as you
            feel able to, as long as {"it's"} not too strenuous.</Paragraph>,
        ];
      } else {
        activityTitle = 'Great work';
        activityStrap = 'You\'re doing great!';
        activityBody = [
          <Paragraph key="id-1">{"It's"} recommended that we take 30 minutes of
            moderate exercise, five days a week. {"You've"} aced this!</Paragraph>,
          <Paragraph key="id-2">Maintaining your usual activity during
            pregnancy is a great way to stay fit and healthy. {"It's"} safe to stay active for as long as you
            feel able to, as long as you don’t make it too strenuous.</Paragraph>,
        ];
      }

      // BMI + PREGNANT
      if (bmi < bmiUnderweight) {
        bmiTitle = 'We can help make a difference';
        bmiStrap = 'We can help.';
        bmiBody = [
          <Paragraph key="id-1">Your BMI is {bmi}, which suggests that your
            weight may be too low.</Paragraph>,
          <Paragraph key="id-2">Eating a balanced diet can help you to achieve
            a healthy weight, giving you and your baby the nutrients you need for good health. Try exploring
            the resources on our website to find out more about healthy eating.</Paragraph>,
          <Paragraph key="id-3">Need a bit of extra support to work out how to
            eat well? Contact us to find out how our LiveWell Coaches could help.</Paragraph>,
        ];
      } else if (bmi >= bmiUnderweight && bmi < bmiOverweight) {
        bmiTitle = "You've aced this one!";
        bmiStrap = 'You\'re doing great!';
        bmiBody = [
          <Paragraph key="id-1">Your BMI is {bmi}, which falls within the
            healthy range. This is good news for you and your baby, though naturally you should expect
            this to increase during the pregnancy.</Paragraph>,
          <Paragraph key="id-2">Keep up the good work with a balanced diet to
            get the nutrients you and your baby need.</Paragraph>,
        ];
      } else if (bmi >= bmiOverweight && bmi < bmiObeseweight) {
        bmiTitle = 'Take care of yourself';
        bmiStrap = 'We can help.';
        bmiBody = [
          <Paragraph key="id-1">Your BMI is {bmi}. This is above the usual
            healthy range, which may or may not be related to your pregnancy. Though weight loss {"isn't"}
            advisable whilst pregnant, {"it's"} important to eat well, get the nutrients that you and your
            baby need and prevent excessive weight gain. Your midwife will be able to advise you on a
            healthy weight approach during pregnancy.</Paragraph>,
          <Paragraph key="id-2">Explore our website for ideas on how to eat
            well, and if you need some extra support, contact us a to find out how a LiveWell Coach
            could help.</Paragraph>,
        ];
      } else {
        bmiTitle = 'Take care of yourself';
        bmiStrap = 'We can help.';
        bmiBody = [
          <Paragraph key="id-1">Your BMI is {bmi}, which could mean you are at
            increasing risk of some potentially serious health conditions.</Paragraph>,
          <Paragraph key="id-2">Whilst it is not advisable to lose weight
            during pregnancy, a healthy lifestyle can improve your health and help give your baby the best
            start in life.</Paragraph>,
          <Paragraph key="id-3">You may want to talk to your midwife about the
            best way to manage your weight during your pregnancy. Alternatively, contact us to find out how
            a LiveWell Coach could help you develop healthier habits.</Paragraph>,
        ];
      }

      // SMOKING + PREGNANT
      if (smoking === 'No') {
        smokeTitle = 'Congratulations on being smoke free!';
        smokeStrap = 'You\'re doing great!';
        smokeBody = [
          <Paragraph key="id-1">Being a non-smoker is one of the best things
            you can do for your health, your baby and your bank balance. Good work!</Paragraph>,
        ];
      } else if (smoking === 'Yes' && smokingstop === 'Yes') {
        smokeTitle = 'We\'re here for you';
        smokeStrap = 'We can help.';
        smokeBody = [
          <Paragraph key="id-1">Stopping smoking is the best thing you can do
            for you and your baby. We know that it can be difficult to stop smoking. But we also know that
            you want to give your baby the best possible start in life. No matter what stage {"you're"} at
            in your pregnancy, {"it's"} never too late to stop smoking. Cutting down is not enough, as the
            chemicals from smoking will still harm their development.</Paragraph>,
          <Paragraph key="id-2">When you stop smoking, both you and your baby
            will feel the benefits immediately. {"There'll"} be more oxygen in your blood, making you and
            your baby much healthier.</Paragraph>,
          <Paragraph key="id-3">Speak to your midwife who can put you in touch
            with a specialist to help you quit. With this help, you’ll be 4 times more likely to quit than
            going it alone.</Paragraph>,
        ];
      } else {
        smokeTitle = 'Stopping smoking is the best thing you can do for you and your baby.';
        smokeStrap = 'We can help.';
        smokeBody = [
          <Paragraph key="id-1">It is up to you to decide when {"you're"} ready
            and how to quit. Smoking can greatly increase your chances of having a heart attack or stroke.
            Your risk of lung disease, coronary heart disease, and other problems including cancer are much
            higher.</Paragraph>,
          <Paragraph key="id-2">We are here to help you whenever you feel ready
            to start thinking about it.</Paragraph>,
        ];
      }
    } else {
      // ALCOHOL + NOT PREGNANT
      if (units === 0) {
        alcoholTitle = "You've aced this one!";
        alcoholStrap = 'You\'re doing great!';
        alcoholBody = [
          <Paragraph key="id-1">Great stuff! Being alcohol-free is the only
            way to guarantee {"we're"} not harming our health with our drinking. Keep up the good
            work.</Paragraph>,
        ];
      } else if (units > 0 && units <= 7) {
        alcoholTitle = "You're doing great!";
        alcoholStrap = 'You\'re doing great!';
        alcoholBody = [
          <Paragraph key="id-1">Great stuff! It seems that your drinking
            levels are within the {"'low-risk'"} category, which means {"you're"} unlikely to be harming your
            health. Keep up the good work.</Paragraph>,
        ];
      } else if (units > 7 && units <= 19) {
        alcoholTitle = 'We can help make a difference!';
        alcoholStrap = 'We can help.';
        alcoholBody = [
          <Paragraph key="id-1">From what you have told us, it’s possible that
            {"you're"} drinking to levels that are increasing your risk of certain health problems.</Paragraph>,
          <Paragraph key="id-2">It is recommended that we have 2 or more
            alcohol-free days every week and {"don't"} consume more than 14 units in a week.</Paragraph>,
          <Paragraph key="id-3">What changes could you make to cut down and
            improve your wellbeing? Follow the link to My LiveWell for personalised suggestions.</Paragraph>,
        ];
      } else {
        alcoholTitle = 'We can help make a difference!';
        alcoholStrap = 'We can help.';
        alcoholBody = [
          <Paragraph key="id-1">From what you have told us, you are drinking at
            levels which could be harming your health and it is possible that you have developed a
            dependence on alcohol.</Paragraph>,
          <Paragraph key="id-2">We recommend that you contact a local
            specialist service to find out more and check whether you are suffering from alcohol
            dependency.</Paragraph>,
          <Paragraph key="id-3">To find out more about the support available to
            you, please contact us on 01305 233 105. You can also follow the link to My LiveWell to develop your
            action plan for cutting down and find out what support is available.</Paragraph>,
        ];
      }

      // ACTIVITY + PREGNANT
      if (activity >= 0 && activity < 4) {
        activityTitle = 'We can help make a difference';
        activityStrap = 'We can help.';
        activityBody = [
          <Paragraph key="id-1">Being active on a regular basis can be tough,
            but it works wonders for our health. {"We're"} here to help you take steps to a healthier
            lifestyle.</Paragraph>,
          <Paragraph key="id-2">{"It's"} recommended that we take 30 minutes of
            moderate exercise, five days a week. Anything that increases your heart rate or gets you feeling
            hot or out of breath counts. Even a brisk walk, spot of gardening or house work will help.</Paragraph>,
          <Paragraph key="id-3">What commitment could you make to bring some
            activity to your daily life? Follow the link to My LiveWell to create your action plan.</Paragraph>,
        ];
      } else if (activity === 4) {
        activityTitle = 'Well done - not bad!';
        activityStrap = 'You\'re getting there!';
        activityBody = [
          <Paragraph key="id-1">We encourage you to make activity a regular
            habit. The more you do, the healthier you’ll feel. {"It's"} recommended that we take 30 minutes
            of moderate exercise five days a week, so you’re close to this already.</Paragraph>,
          <Paragraph key="id-2">What extra activity could work for you? Explore
            your local options on our Activity Finder and follow the link to My LiveWell to make your
            personal action plan.</Paragraph>,
        ];
      } else {
        activityTitle = 'Great work';
        activityStrap = 'You\'re doing great!';
        activityBody = [
          <Paragraph key="id-1">{"It's"} recommended that we take 30 minutes of
            moderate exercise, five days a week. {"You've"} aced this!</Paragraph>,
          <Paragraph key="id-2">We encourage you to keep up with regular
            activity, which will continue to work wonders for your health.</Paragraph>,
          <Paragraph key="id-3">If you {"haven't"} already, consider adopting
            strengthening activities that work your major muscles (legs, hips, back, abdomen, chest, shoulders
            and arms). Even carrying heavy shopping counts! Look for new ways to get active on our LiveWell
            Finder.</Paragraph>,
        ];
      }

      // BMI + NOT PREGNANT
      if (bmi < bmiUnderweight) {
        bmiTitle = 'We can help make a difference';
        bmiStrap = 'We can help.';
        bmiBody = [
          <Paragraph key="id-1">Your BMI is {bmi}, which suggests that your
            weight may be too low. This can lead to poor immunity, fragile bones and feeling
            tired.</Paragraph>,
          <Paragraph key="id-2">If diet is the cause of your low weight, a
            balanced diet can help you overcome this. Try exploring the resources on our website to find out
            more about healthy eating.</Paragraph>,
          <Paragraph key="id-3">Need a bit of extra support to work out how to
            eat well? Contact our friendly team to find out how our LiveWell Coaches could help.</Paragraph>,
        ];
      } else if (bmi >= bmiUnderweight && bmi < bmiOverweight) {
        bmiTitle = "You've aced this one!";
        bmiStrap = 'You\'re doing great!';
        bmiBody = [
          <Paragraph key="id-1">Good news! Your BMI is {bmi}, which falls
            within the healthy range.</Paragraph>,
          <Paragraph key="id-2">Being a healthy weight reduces your risk of
            developing certain health conditions. Keep up the good work!</Paragraph>,
        ];
      } else if (bmi >= bmiOverweight && bmi < bmiObeseweight) {
        bmiTitle = 'We can help make a difference';
        bmiStrap = 'We can help.';
        bmiBody = [
          <Paragraph key="id-1">Your BMI is {bmi}, which falls into
            the {"'overweight'"} category. What changes could you make to work towards a healthier new
            you?</Paragraph>,
          <Paragraph key="id-2">There are all sorts of things you can do to
            achieve a healthy weight. Try exploring the resources on our website or joining <a
                href="https://www.facebook.com/groups/ourlivewell/">Our LiveWell</a> for support from others
            in the same boat</Paragraph>,
          <Paragraph key="id-3">If you need some extra support, contact us to
            find out how a LiveWell Coach could help you.</Paragraph>,
        ];
      } else {
        bmiTitle = 'We can help make a difference';
        bmiStrap = 'We can help.';
        bmiBody = [
          <Paragraph key="id-1">Your BMI is {bmi}, which means you are at
            increasing risk of some potentially serious health conditions such as diabetes and heart
            problems.</Paragraph>,
          <Paragraph key="id-2">The best way to lose weight is to make small,
            realistic changes to your diet and physical activity levels. The LiveWell team is here to
            help.</Paragraph>,
          <Paragraph key="id-3">Contact us to explore the support options we
            can offer you. This could include a free 3-month kick start at a weight management group or one
            to one telephone coaching from our LiveWell Coaches.</Paragraph>,
        ];
      }

      // SMOKING + NOT PREGNANT
      if (smoking === 'No') {
        smokeTitle = 'Congratulations on being smoke free!';
        smokeStrap = 'You\'re doing great!';
        smokeBody = [
          <Paragraph key="id-1">Being a non-smoker is one of the best things
            you can do for your health, your family and your bank balance. Good work!</Paragraph>,
        ];
      } else if (smoking === 'Yes' && smokingstop === 'Yes') {
        smokeTitle = 'We\'re here for you';
        smokeStrap = 'We can help.';
        smokeBody = [
          <Paragraph key="id-1">Quitting is the best thing you’ll ever
            do!</Paragraph>,
          <Paragraph key="id-2" >Prepare for the day you quit and avoid
            temptation: choose a quit date {"that's"} unlikely to be stressful, make sure you {"don't"}
            have any cigarettes, lighters or matches on you and avoid places or situations that will
            make you want to smoke.</Paragraph>,
          <Paragraph key="id-3">Did you know {"you're"} 4 times more likely to quit
            if you use your local 12-week stop smoking programme than going cold turkey? Contact our
            friendly team to find out more about the services in your area.</Paragraph>,
        ];
      } else {
        smokeTitle = "Just remember we're here for you!";
        smokeStrap = 'We can help.';
        smokeBody = [
          <Paragraph key="id-1">It is up to you to decide when {"you're"} ready
            and how to quit. Smoking can greatly increase your chances of having a heart attack or stroke.
            Your risk of lung disease, coronary heart disease, and other problems including cancer are much
            higher.</Paragraph>,
          <Paragraph key="id-2">We are here to help you whenever you feel ready
            to start thinking about it.</Paragraph>,
        ];
      }
    }

    let alcoholQuestion = [<Paragraph key={0} classes="section-text"><strong>Would you like help with this?</strong></Paragraph>,
      <Field
        key={1}
        name="pathway_alcohol"
        type="radiogroup"
        centered
        params={{dataKey: 'LeaveMessage', name: 'pathway_alcohol', classes: 'textUppercase yellow'}}
        component={renderField}
        onChange={(e, ks) => this.togglePathway({ val: ks, pathway: 'pathway_alcohol' })}
      />,
    ];
    if (this.props.units === 0) {
      alcoholQuestion = '';
    }

    let smokingQuestion = [<Paragraph key={0} classes="section-text"><strong>Would you like help with this?</strong></Paragraph>,
      <Field
        key={1}
        name="pathway_smoking"
        type="radiogroup"
        centered
        params={{dataKey: 'LeaveMessage', name: 'pathway_smoking', classes: 'textUppercase red'}}
        component={renderField}
        onChange={(e, ks) => this.togglePathway({ val: ks, pathway: 'pathway_smoking' })}
      />,
    ];

    let amount = smokingamount;
    if (this.props.smoking === 'No') {
      smokingQuestion = '';
      amount = '0';
    }
    let smokeType = '';
    if (this.props.smokingtype === '1517') {
      smokeType = 'Cigarettes';
    } else if (this.props.smokingtype === '1518') {
      smokeType = 'Cigars';
    } else if (this.props.smokingtype === '1617') {
      smokeType = 'Non-smoker';
    } else if (this.props.smokingtype === '1520') {
      smokeType = 'Pipe';
    } else if (this.props.smokingtype === '1521') {
      smokeType = 'Roll-ups';
    } else if (this.props.smokingtype === '1519') {
      smokeType = 'e-Cigarettes';
    }

    return (
        <div
            className='register-all-results'
            ref={(sectionRef) => {
                this.sectionRef = sectionRef;
            }}>
            <Progress step='all' />
            <div className='register-block step-one-block'>
                <Heading tag='h1' classes='header-text purple' center>
                    <span id='registerComplete'>Congratulations!</span>
                </Heading>
                <Paragraph classes='sub-header-text' center>
                    You are now registered with LiveWell Dorset
                </Paragraph>
                <Paragraph classes='step-text' center>
                    Step 1. View your results
                </Paragraph>

                <form onSubmit={handleSubmit} className='form-register'>
                    <div className='results results-wrap flex-row four-cols'>
                        <div className='item green'>
                            <div className='item-summary justifycentre'>
                                <Paragraph classes='item-image'>
                                    <img src='https://livewelldorset.s3.amazonaws.com/Pathways/getactive-roundal.png' alt='' width='50' height='50' />
                                </Paragraph>
                                <Paragraph classes='item-title larger'>Activity</Paragraph>
                                <Paragraph classes='item-times'>{activity}</Paragraph>
                                <Paragraph classes='item-unit'>activities per week</Paragraph>
                            </div>

                            <Paragraph classes='content-title'>{activityTitle}</Paragraph>
                            <Readmore>{activityBody}</Readmore>
                            <Paragraph classes='section-text'>
                                <strong>Would you like help with this?</strong>
                            </Paragraph>
                            <Field name='pathway_activity' type='radiogroup' centered params={{ dataKey: 'LeaveMessage', name: 'pathway_activity', classes: 'textUppercase' }} component={renderField} onChange={(e, ks) => this.togglePathway({ val: ks, pathway: 'pathway_activity' })} />
                        </div>

                        <div className='item purple'>
                            <div className='item-summary justifycentre'>
                                <Paragraph classes='item-image'>
                                    <img src='https://livewelldorset.s3.amazonaws.com/Pathways/loseweight-roundal.png' alt='' width='50' height='50' />
                                </Paragraph>
                                <Paragraph classes='item-title larger'>Weight</Paragraph>
                                <Paragraph classes='item-times'>{bmi}</Paragraph>
                                <Paragraph classes='item-unit'>BMI</Paragraph>
                            </div>

                            <Paragraph classes='content-title'>{bmiTitle}</Paragraph>
                            <Readmore>{bmiBody}</Readmore>
                            <Paragraph classes='section-text'>
                                <strong>Would you like help with this?</strong>
                            </Paragraph>
                            <Field name='pathway_weight' type='radiogroup' centered params={{ dataKey: 'LeaveMessage', name: 'pathway_weight', classes: 'textUppercase purple' }} component={renderField} onChange={(e, ks) => this.togglePathway({ val: ks, pathway: 'pathway_weight' })} />
                        </div>

                        <div className='item red'>
                            <div className='item-summary justifycentre'>
                                <Paragraph classes='item-image'>
                                    <img src='https://livewelldorset.s3.amazonaws.com/Pathways/quitsmoking-roundal.png' alt='' width='50' height='50' />
                                </Paragraph>
                                <Paragraph classes='item-title larger'>Smoking</Paragraph>
                                <Paragraph classes='item-times'>{amount}</Paragraph>
                                <Paragraph classes='item-unit'>{smokeType} per day</Paragraph>
                            </div>

                            <Paragraph classes='content-title'>{smokeTitle}</Paragraph>
                            <Readmore>{smokeBody}</Readmore>
                            {smokingQuestion}
                        </div>

                        <div className='item yellow'>
                            <div className='item-summary justifycentre'>
                                <Paragraph classes='item-image'>
                                    <img src='https://livewelldorset.s3.amazonaws.com/Pathways/drink-less.png' alt='' width='50' height='50' />
                                </Paragraph>
                                <Paragraph classes='item-title'>Drinking</Paragraph>
                                <Paragraph classes='item-times'>{units}</Paragraph>
                                <Paragraph classes='item-unit'>units per week</Paragraph>
                            </div>

                            <Paragraph classes='content-title'>{alcoholTitle}</Paragraph>
                            <Readmore>{alcoholBody}</Readmore>
                            {alcoholQuestion}
                        </div>
                    </div>
                </form>
            </div>

            <div className='justifycentre register-block step-two-block'>
                <Paragraph classes='step-text'>Next Steps</Paragraph>
                <Paragraph classes='larger width-limited'>Thank you for registering with us, we will be in touch.</Paragraph>
                <Paragraph classes='larger width-limited'>If you {"haven't"} heard from us in 2-3 working days please contact us</Paragraph>
                {/* <Paragraph classes='button-groups'>
                    <Button classes={['color-button']} label='My Livewell Profile' onClick={this.props.dashboard} /> */}
                {/* <Button classes={['underline-button']} label="Back" onClick={this.props.previousPage} /> */}
                {/* </Paragraph> */}
            </div>

            {/* <div className='register-block expanded larger mylivewell'>
                <div className='expanded-bg' />
                <div className='expanded-content'>
                    <Heading classes='header-text smaller'>My Livewell</Heading>
                    <Paragraph classes='larger'>Your place for personalised tips and tools to LiveWell. Plan your journey, track your progress, start your story. Let us show you how it works.</Paragraph>
                    <Paragraph classes='button-groups'>
                        <Button classes={['color-button outline white align-left']} label='My Livewell Profile' onClick={this.props.dashboard} />
                    </Paragraph>
                    <img className='iphone-image' src={iphoneImg} alt='my livewell iphone screenshot' width='263.76' height='530' />
                </div>
            </div> */}

            <div className='register-block expanded larger talktous'>
                <div className='expanded-bg' />
                <div className='expanded-content'>
                    <Heading classes='header-text smaller'>Talk to us</Heading>
                    <Paragraph classes='tel-text'>0800 840 1628</Paragraph>
                    <Paragraph classes='tel-text'>01305 233 105</Paragraph>
                    <Paragraph classes='larger time-info-text'>Monday - Friday 9am - 6:30pm</Paragraph>
                </div>
            </div>
        </div>
    );
  }
}

AllResults.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  // previousPage: PropTypes.func.isRequired,
  smoking: PropTypes.string.isRequired,
  smokingstop: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  weight: PropTypes.number.isRequired,
  activity: PropTypes.number.isRequired,
  score: PropTypes.number.isRequired,
  pregnant: PropTypes.string.isRequired,
  dashboard: PropTypes.func.isRequired,
};

export default reduxForm({
  form: 'registration', // <------ same form name
  destroyOnUnmount: false, // <------ preserve form data
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
})(AllResults);



// WEBPACK FOOTER //
// ./src/Components/Register/FormPages/AllResults.jsx